"use client"

import React from "react"
import { AlertTriangle, Info, X } from "lucide-react"
import { QualityResult } from "../util/image-quality-checker"

interface QualityWarningBannerProps {
    qualityResult: QualityResult | null
    onDismiss: () => void
    dismissed: boolean
}

/**
 * Displays a simple quality warning for images before OCR processing.
 * Shows one unified message regardless of how many issues are detected.
 */
export default function QualityWarningBanner({ 
    qualityResult, 
    onDismiss, 
    dismissed 
}: QualityWarningBannerProps) {
    // Don't show if no result, no warnings, or dismissed
    if (!qualityResult || qualityResult.warnings.length === 0 || dismissed) {
        return null
    }

    const { warnings } = qualityResult
    
    // Determine overall severity for banner color
    const hasWarning = warnings.some(w => w.severity === 'warning')
    
    // Get appropriate icon and colors based on severity
    const Icon = hasWarning ? AlertTriangle : Info
    const bgColor = hasWarning 
        ? "bg-amber-50 border-amber-200" 
        : "bg-blue-50 border-blue-200"
    const iconColor = hasWarning ? "text-amber-500" : "text-blue-500"
    const textColor = hasWarning ? "text-amber-800" : "text-blue-800"
    const suggestionColor = hasWarning ? "text-amber-600" : "text-blue-600"

    // Single unified message
    const message = hasWarning 
        ? "Jakość zdjęcia może nie być optymalna"
        : "Zdjęcie może być lepszej jakości"
    const suggestion = "Dla lepszych wyników spróbuj ponowić zdjęcie"

    return (
        <div 
            className={`${bgColor} border rounded-lg p-3 mb-3 animate-in fade-in slide-in-from-top-2 duration-300`}
            role="alert"
        >
            <div className="flex items-start gap-2">
                <Icon className={`${iconColor} h-5 w-5 flex-shrink-0 mt-0.5`} />
                
                <div className="flex-1 min-w-0">
                    <p className={`${textColor} text-sm font-medium`}>
                        {message}
                    </p>
                    <p className={`${suggestionColor} text-xs mt-0.5`}>
                        {suggestion}
                    </p>
                </div>
                
                <button
                    onClick={onDismiss}
                    className={`${iconColor} hover:opacity-70 p-1 -mr-1 -mt-1 flex-shrink-0 transition-opacity`}
                    aria-label="Zamknij ostrzeżenie"
                >
                    <X className="h-4 w-4" />
                </button>
            </div>
            
            {/* Quality score indicator (subtle) */}
            <div className="mt-2 flex items-center gap-2">
                <div className="flex-1 h-1.5 bg-white/50 rounded-full overflow-hidden">
                    <div 
                        className={`h-full transition-all duration-500 ${
                            qualityResult.overallScore >= 70 ? 'bg-green-400' :
                            qualityResult.overallScore >= 40 ? 'bg-amber-400' :
                            'bg-red-400'
                        }`}
                        style={{ width: `${qualityResult.overallScore}%` }}
                    />
                </div>
                <span className={`text-xs ${suggestionColor} whitespace-nowrap`}>
                    Jakość: {qualityResult.overallScore}%
                </span>
            </div>
        </div>
    )
}
